import React, { useState } from 'react';
import { 
  FaTerminal, 
  FaCodeBranch, 
  FaTags, 
  FaInfoCircle, 
  FaArrowRight, 
  FaKeyboard 
} from 'react-icons/fa';

// Mock schema options for mapping context
const AVAILABLE_DOMAINS = ["Backend Systems", "Database Architecture", "Data Pipelines", "Frontend Engineering"];
const AVAILABLE_TOOLS = ["Knex.js", "PostgreSQL", "Gemini API", "AWS Lambda", "Docker"];
const AVAILABLE_SKILLS = ["LLM Structured Extraction", "Vector Embeddings", "Database Migrations", "Schema Validation"];

export default function DailyLoggingEngine({ onCommitSuccess }) {
  const [logText, setLogText] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedTool, setSelectedTool] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const characterCount = logText.length;
  const isReadyToCommit = logText.trim().length > 10 && selectedDomain;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isReadyToCommit) return;

    setIsSubmitting(true);
    
    // Simulating slight engineering pipeline latency before jumping to Screen 3 (Quiz Loop)
    setTimeout(() => {
      setIsSubmitting(false);
      if (onCommitSuccess) {
        onCommitSuccess({
          text: logText,
          domain: selectedDomain,
          tool: selectedTool,
          skill: selectedSkill
        });
      }
    }, 800);
  };

  return (
    <div style={styles.container}>
      {/* Engine Workspace Wrapper */}
      <div style={styles.workspaceBox}>
        
        {/* Terminal Header Bar */}
        <div style={styles.terminalHeader}>
          <div style={styles.windowButtons}>
            <span style={{ ...styles.dot, backgroundColor: '#ff5f56' }}></span>
            <span style={{ ...styles.dot, backgroundColor: '#ffbd2e' }}></span>
            <span style={{ ...styles.dot, backgroundColor: '#27c93f' }}></span>
          </div>
          <div style={styles.terminalTitle}>
            <FaTerminal style={styles.titleIcon} /> live-resume --log-session
          </div>
          <div style={styles.terminalGhost}></div>
        </div>

        {/* Form Body Layout */}
        <form onSubmit={handleSubmit} style={styles.formWorkspace}>
          
          {/* Main Input Textarea simulating a clean prompt entry */}
          <div style={styles.inputAreaWrapper}>
            <div style={styles.promptLine}>
              <span style={styles.promptSymbol}>$</span>
              <label htmlFor="work-log" style={styles.ghostLabel}>Verify raw task payload:</label>
            </div>
            <textarea
              id="work-log"
              value={logText}
              onChange={(e) => setLogText(e.target.value)}
              placeholder="What proof-of-work did you ship today? (e.g., Refactored Knex.js table migrations to isolate tracking variables...)"
              style={styles.terminalTextArea}
              disabled={isSubmitting}
              maxLength={400}
            />
            <div style={styles.charCounter}>
              <FaKeyboard style={{ marginRight: '4px' }} /> {characterCount}/400 chars
            </div>
          </div>

          {/* Context Mapping Metadata Matrix */}
          <div style={styles.metaGrid}>
            
            {/* Domain Context */}
            <div style={styles.selectGroup}>
              <label style={styles.selectLabel}>
                <FaCodeBranch style={styles.labelIcon} /> Target Domain <span style={styles.required}>*</span>
              </label>
              <select 
                value={selectedDomain} 
                onChange={(e) => setSelectedDomain(e.target.value)}
                style={styles.customSelect}
                disabled={isSubmitting}
              >
                <option value="">-- select domain reference --</option>
                {AVAILABLE_DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {/* Core Tool Chain */}
            <div style={styles.selectGroup}>
              <label style={styles.selectLabel}>
                <FaTags style={styles.labelIcon} /> Primary Tool Stack
              </label>
              <select 
                value={selectedTool} 
                onChange={(e) => setSelectedTool(e.target.value)}
                style={styles.customSelect}
                disabled={isSubmitting}
              >
                <option value="">-- select structural tool --</option>
                {AVAILABLE_TOOLS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Granular Skill Taxonomy */}
            <div style={styles.selectGroup}>
              <label style={styles.selectLabel}>
                <FaTags style={styles.labelIcon} /> Granular Focus Skill
              </label>
              <select 
                value={selectedSkill} 
                onChange={(e) => setSelectedSkill(e.target.value)}
                style={styles.customSelect}
                disabled={isSubmitting}
              >
                <option value="">-- select focus skill --</option>
                {AVAILABLE_SKILLS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

          </div>

          {/* Guidelines Notice Info Callout */}
          <div style={styles.infoCallout}>
            <FaInfoCircle style={styles.infoIcon} />
            <p style={styles.infoText}>
              Submitting triggers the micro-validation sequence loop. Be precise—your textual description informs the custom test suite queries.
            </p>
          </div>

          {/* Action Confirmation Footer Bar */}
          <div style={styles.footerActionRow}>
            <button
              type="submit"
              disabled={!isReadyToCommit || isSubmitting}
              style={{ 
                ...styles.commitButton, 
                ...(!isReadyToCommit ? styles.disabledButton : {}),
                ...(isSubmitting ? styles.loadingButton : {})
              }}
            >
              {isSubmitting ? "Generating Question Stack..." : "Commit Log to Ledger"} 
              {!isSubmitting && <FaArrowRight style={{ marginLeft: '8px', fontSize: '12px' }} />}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0d1117',
    padding: '24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  workspaceBox: {
    width: '100%',
    maxWidth: '720px',
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
  },
  terminalHeader: {
    backgroundColor: '#21262d',
    borderBottom: '1px solid #30363d',
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  windowButtons: {
    display: 'flex',
    gap: '8px',
    width: '60px'
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%'
  },
  terminalTitle: {
    fontSize: '13px',
    color: '#8b949e',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  titleIcon: {
    fontSize: '12px'
  },
  terminalGhost: {
    width: '60px'
  },
  formWorkspace: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputAreaWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '14px'
  },
  promptLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  promptSymbol: {
    color: '#58a6ff',
    fontWeight: 'bold',
    fontSize: '16px'
  },
  ghostLabel: {
    color: '#8b949e',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '600'
  },
  terminalTextArea: {
    width: '100%',
    minHeight: '110px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#c9d1d9',
    fontSize: '15px',
    lineHeight: '1.5',
    fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
    resize: 'vertical'
  },
  charCounter: {
    textAlign: 'right',
    fontSize: '11px',
    color: '#8b949e',
    borderTop: '1px solid #21262d',
    paddingTop: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  selectGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  selectLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#c9d1d9',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  labelIcon: {
    color: '#8b949e',
    fontSize: '12px'
  },
  required: {
    color: '#f78166'
  },
  customSelect: {
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '8px 12px',
    color: '#c9d1d9',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer'
  },
  infoCallout: {
    backgroundColor: 'rgba(56, 139, 253, 0.1)',
    border: '1px solid rgba(56, 139, 253, 0.2)',
    borderRadius: '6px',
    padding: '12px 16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start'
  },
  infoIcon: {
    color: '#58a6ff',
    marginTop: '2px',
    flexShrink: 0
  },
  infoText: {
    margin: 0,
    fontSize: '13px',
    color: '#58a6ff',
    lineHeight: '1.4'
  },
  footerActionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: '1px solid #20262e',
    paddingTop: '16px'
  },
  commitButton: {
    backgroundColor: '#238636',
    border: '1px solid rgba(240,246,252,0.1)',
    borderRadius: '6px',
    color: '#ffffff',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s'
  },
  disabledButton: {
    backgroundColor: '#213527',
    color: '#8b949e',
    cursor: 'not-allowed',
    borderColor: 'transparent'
  },
  loadingButton: {
    backgroundColor: '#9e6a28',
    cursor: 'wait'
  }
};